import { plural, t } from "@lingui/core/macro";
import {
  ComparatorResult,
  EMPTY_ARRAY,
  EMPTY_STRING,
  isArraysEqual,
  stringComparator,
  trimToken,
} from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type {
  ConditionOperator,
  CreateFieldInstanceValueInputs,
  FieldAllowedValueString,
  FieldFields,
  FieldInstanceFields,
  FieldInstanceFieldsWithBaseValues,
  FieldInstanceValueFieldsFieldInstanceMultiValueString,
  FieldInstanceValueInputType,
  PropertyDataType,
  PropertyTypeFields,
  SpectrumFieldVersionFields,
  TableFieldInstanceFields,
  UpdateFieldInstanceValueInputs,
  UpdateStartingConditionsInputs,
  ViewFilterFields,
} from "@regrello/graphql-api";
import { RegrelloIcon } from "@regrello/ui-core";
import type { FieldPath, FieldValues } from "react-hook-form";
import type { FieldInstanceBaseFields } from "../../../../../types";
import { getFieldInstanceId } from "../../../../../utils/customFields/getFieldInstanceId";
import {
  getRegrelloDefaultFilterDefinitionMultiSelectValue,
  getRegrelloFilterDefinitionMultiSelectValue,
} from "../../../../../utils/filters/v2/filterKinds/core/filterMultiSelect";
import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";
import { RegrelloInactiveBadge } from "../../../components/inactiveBadge/RegrelloInactiveBadge";
import { RegrelloControlledFormFieldMultiSelect } from "../../formFields/controlled/RegrelloControlledFormFieldMultiSelect";
import { RegrelloCustomFieldMultiValuePopover } from "../components/RegrelloCustomFieldMultiValuePopover";
import { CustomFieldPluginRegistrar } from "./registry/customFieldPluginRegistrar";
import {
  type CustomFieldFrontendSelectableOption,
  INACTIVE_SELECTABLE_OPTION_ID,
} from "./types/CustomFieldFrontendSelectableOption";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
  RenderDisplayValueOptions,
} from "./types/CustomFieldPlugin";
import { createViewColumnsFromField } from "./utils/createViewColumnsFromField";
import { DEFAULT_INPUT_TYPE_IF_NO_VALUE, getConditionOperatorsByType } from "./utils/customFieldConstants";
import { extractAllowedValuesFromFieldVersionForFrontend } from "./utils/extractAllowedValuesForFrontend";
import { extractMultipleTabularArraysOrThrow } from "./utils/extractMultipleTabularArraysOrThrow";
import { extractSelectedValuesOrThrow } from "./utils/extractSelectedValuesOrThrow";
import {
  getIsFieldInstanceFields,
  getIsFieldInstanceValueWithCrossWorkflowFields,
} from "./utils/fieldInstanceTypeguards";
import { getUpdateStartingConditionsInputsForEmptyOperators } from "./utils/getUpdateStartingConditionsInputsForEmptyOperators";
import { extractAllowedValues, normalizeSelectValue, validateRequired } from "./utils/validateBulkUploadCellValueUtils";

// TODO Misc: Turn the following into warnings and handle gracefully so the app doesn't crash in
// case of field misconfiguration.
const ERROR_INVALID_FIELD = "Provided 'multiSelect' field is invalid";
const WARNING_INVALID_FORM_VALUE = "Provided 'multiSelect'-field form value is not a valid array of selectable options";
const ERROR_INVALID_VALUE_TYPE =
  "Provided 'multiSelect' field instance value must have type 'FieldInstanceValueString'";
const WARNING_INVALID_OPERATOR_TYPE = "Provided operator type is invalid for 'multiselect' fields";
const WARNING_UNEXPECTED_EMPTY_FORM_VALUE =
  "Provided 'multiselect' field form value must not be empty given the provided operator type";
const WARNING_UNEXPECTED_DEFINED_FORM_VALUE =
  "Provided 'multiselect' field form value must not be defined given the provided operator type";

function canProcessPropertyDataType(propertyDataType: PropertyDataType): boolean {
  return propertyDataType === "STRING";
}

function canProcessField(field: FieldFields): boolean {
  return (
    field.isMultiValued && field.allowedValues.length > 0 && canProcessPropertyDataType(field.propertyType.dataType)
  );
}

type MultiSelectPluginFrontendValue = CustomFieldFrontendSelectableOption[];
type MultiSelectFieldPluginType = CustomFieldPlugin<MultiSelectPluginFrontendValue>;

const getConditionOperators: MultiSelectFieldPluginType["getConditionOperators"] = () =>
  getConditionOperatorsByType("MULTISELECT");

function renderDisplayValueInternal(
  _fieldVersion: SpectrumFieldVersionFields,
  values: CustomFieldFrontendSelectableOption[],
  options?: RenderDisplayValueOptions,
) {
  if (options?.context === "table" || options?.context === "inlineTruncate" || options?.context === "dataTab") {
    return (
      <span>
        {values.map((allowedValue, index) =>
          getOptionFrontendDisplayValue(
            allowedValue,
            index === values.length - 1
              ? ""
              : index === values.length - 2 && options?.lastStringSeparator != null
                ? ` ${options.lastStringSeparator} `
                : ", ",
          ),
        )}
      </span>
    );
  }

  return (
    // Wrap all the multiselect values in an element to ensure they do not become flex children.
    <div>
      {values.map(({ id, value, displayValue }, index) => {
        return (
          <div
            // (zstanik): Not ideal to use `index` here, but the selected options aren't guaranteed
            // to have unique IDs and values.
            key={`${id}-${value}-${index}`}
            data-testid={DataTestIds.CUSTOM_FIELD_MULTISELECT_VALUE}
          >
            {`• ${displayValue}`}
            {id === INACTIVE_SELECTABLE_OPTION_ID && <RegrelloInactiveBadge />}
          </div>
        );
      })}
    </div>
  );
}

const renderDisplayValueFromStringValue: MultiSelectFieldPluginType["renderDisplayValueFromStringValue"] = (
  fieldVersion,
  value,
  options,
) => {
  if (options?.transformToFrontendValue == null) {
    console.warn(
      "No transformToFrontendValue function was provided; this is necessary for this field type." +
        "Returning the value as is.",
    );
    return value;
  }

  const allowedValues = options.transformToFrontendValue(value);

  return renderDisplayValueInternal(fieldVersion, allowedValues, options);
};

const renderDisplayValue: MultiSelectFieldPluginType["renderDisplayValue"] = (fieldInstance, options = {}) => {
  const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance, {
    includeInactiveOptions: true,
  });
  return renderDisplayValueInternal(fieldInstance.spectrumFieldVersion, frontendFieldInstance.values, options);
};

const sortComparator: MultiSelectFieldPluginType["sortComparator"] = (
  fieldInstance1,
  fieldInstance2,
  direction = "asc",
): ComparatorResult => {
  if (direction === "desc") {
    return MultiSelectFieldPlugin.sortComparator(fieldInstance2, fieldInstance1, "asc");
  }

  if (fieldInstance1 == null) {
    return ComparatorResult.BEFORE;
  }

  if (fieldInstance2 == null) {
    return ComparatorResult.AFTER;
  }

  const fieldPlugin = CustomFieldPluginRegistrar.getPluginForField(fieldInstance1.field);

  const value1 = fieldPlugin.getValueForFrontend(fieldInstance1) as CustomFieldFrontendSelectableOption[];
  const value2 = fieldPlugin.getValueForFrontend(fieldInstance2) as CustomFieldFrontendSelectableOption[];

  const normalizedValue1 = value1.map(({ displayValue }) => displayValue.toLocaleLowerCase()).join("-");
  const normalizedValue2 = value2.map(({ displayValue }) => displayValue.toLocaleLowerCase()).join("-");

  return stringComparator(normalizedValue1, normalizedValue2);
};

/**
 * Describes a custom field that allows the user to select multiple values from an approved list.
 */
export const MultiSelectFieldPlugin: MultiSelectFieldPluginType = {
  uri: "com.regrello.customField.multiSelect",
  version: "1.0.0",

  canProcessField: (field: FieldFields): boolean => {
    return canProcessField(field);
  },

  canProcessFieldInstance: (fieldInstance: FieldInstanceBaseFields): boolean => {
    try {
      translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
      return true;
    } catch (_error) {
      return false;
    }
  },

  canProcessPropertyDataType,

  findPropertyTypeIdFromLoadedPropertyTypeIds: (loadedPropertyTypes: PropertyTypeFields[]): number | undefined => {
    // (clewis): 'Multi-select' fields always hold text/string values.
    return loadedPropertyTypes.find((propertyType) => propertyType.dataType === "STRING")?.id;
  },

  getColumnsForTable: createViewColumnsFromField,

  getConditionOperators,

  getCreateFieldInstanceValueInputsFromFormValue: (
    inputs: GetCreateFieldInstanceValueInputsFromFormValueParams,
  ): CreateFieldInstanceValueInputs => {
    const {
      displayOrder,
      field,
      inputType,
      displayContext,
      sourceFieldInstanceId,
      spectrumFieldVersion,
      value: inputValues,
    } = inputs;
    if (!isValueValid(inputValues)) {
      throw new Error(getErrorMessageWithPayload(WARNING_INVALID_FORM_VALUE, { field, inputType, value: inputValues }));
    }
    if (inputValues == null) {
      return {
        fieldId: field.id,
        inputType,
        displayContext: displayContext ?? "DEFAULT",
        displayOrder,
        spectrumFieldVersionId: spectrumFieldVersion.id,
        sourceFieldInstanceValueId: sourceFieldInstanceId,
      };
    }

    const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(spectrumFieldVersion);

    // (clewis): O(N^2) but this method will be called rarely, so no big deal.
    const validOptions = inputValues.filter((selectedOption) =>
      allowedValues.some(
        (allowedValue) => allowedValue.id === selectedOption.id && allowedValue.value === selectedOption.value,
      ),
    );
    if (validOptions.length !== inputValues.length) {
      console.warn(
        "[MultiSelectFieldPlugin] Received one or more values that do not appear in allowedValues. Silently ignoring them.",
        { field, value: inputValues, validOptions },
      );
    }

    return {
      displayOrder: displayOrder,
      fieldId: field.id,
      stringMultiValue: validOptions.map((element) => element.value),
      inputType,
      displayContext: displayContext ?? "DEFAULT",
      spectrumFieldVersionId: spectrumFieldVersion.id,
      sourceFieldInstanceValueId: sourceFieldInstanceId,
    };
  },

  getCrossWorkflowSinksFieldInstanceIds: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number[] => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSinksFieldInstanceIds;
  },

  getCrossWorkflowSourceFieldInstanceIdFromValue: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSourceFieldInstanceId;
  },

  getFieldDisplayName: (): string => {
    return t`Multi-select`;
  },

  getFilterDefinition: (field: FieldFields) => {
    return getRegrelloDefaultFilterDefinitionMultiSelectValue({
      type: "default",
      values: field.allowedValues.filter(isFieldAllowedValueString).map((value) => ({
        id: value.id,
        label: value.displayStringValue,
        value: value.stringValue,
      })),
    });
  },

  getFilterDefinitionWithValues: (field: FieldFields, filter: ViewFilterFields) => {
    const values = filter.values ?? EMPTY_ARRAY;
    const allowedValues = field.allowedValues.filter(isFieldAllowedValueString).map((value) => ({
      id: value.id,
      label: value.displayStringValue,
      value: value.stringValue,
    }));
    return getRegrelloFilterDefinitionMultiSelectValue(filter.operator, values, allowedValues);
  },

  getEmptyValueForFrontend: (): CustomFieldFrontendSelectableOption[] => {
    return EMPTY_ARRAY;
  },

  getEmptyTabularValueForFrontend: (
    rowNumber: number,
  ): { value: CustomFieldFrontendSelectableOption[]; rowNumber: number } => {
    return { value: EMPTY_ARRAY, rowNumber };
  },

  getHelperText: (): string => {
    return t`Multi-select allows you to select multiple options from predefined options in a dropdown.`;
  },

  getIconName: () => {
    return "multi-select-field";
  },

  getNameTemplateDisplayValueFromFormValue: (value) => {
    if (!isMultiSelectFrontendValue(value) || value == null || value.length === 0) {
      return undefined;
    }
    return value.map((option) => option.displayValue).join(", ");
  },

  getSourceFieldInstance: (_fieldInstance: FieldInstanceFields): FieldInstanceFields | undefined => {
    return undefined;
  },

  getSourceFieldInstanceId: (fieldInstance: FieldInstanceBaseFields): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sourceFieldInstanceId;
  },

  getSinkFieldInstanceIds: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sinksFieldInstanceIds;
  },

  getSourceFieldInstanceInputType: (
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
  ): FieldInstanceValueInputType | undefined => {
    const sourceValue = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return sourceValue.sourceFieldInstanceInputType;
  },

  getUpdateStartingConditionsInputsFromFormValues: (
    leftFieldInstance: FieldInstanceFields,
    value: unknown,
    operator: ConditionOperator,
  ): UpdateStartingConditionsInputs | undefined => {
    if (!isOperatorValid(operator)) {
      console.warn(WARNING_INVALID_OPERATOR_TYPE, {
        leftFieldInstance,
        operator,
      });
      return undefined;
    }

    if (operator === "EMPTY" || operator === "NOT_EMPTY") {
      if (value != null) {
        console.warn(WARNING_UNEXPECTED_DEFINED_FORM_VALUE, {
          leftFieldInstance,
          value,
        });
      }
      return getUpdateStartingConditionsInputsForEmptyOperators(getFieldInstanceId(leftFieldInstance), operator);
    }

    if (value == null || (Array.isArray(value) && value.length === 0)) {
      console.warn(WARNING_UNEXPECTED_EMPTY_FORM_VALUE, { leftFieldInstance, value });
      return undefined;
    }

    if (!isValueValid(value)) {
      console.warn(getErrorMessageWithPayload(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value }));
      return undefined;
    }

    const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(leftFieldInstance.spectrumFieldVersion);

    // (clewis): O(N^2) but this method will be called rarely, so no big deal.
    const validOptions = value.filter((selectedOption) =>
      allowedValues.some(
        (allowedValue) => allowedValue.id === selectedOption.id && allowedValue.displayValue === selectedOption.value,
      ),
    );
    if (validOptions.length !== value.length) {
      console.warn(
        "[MultiSelectFieldPlugin] Received one or more values that do not appear in allowedValues. Silently ignoring them.",
        { leftFieldInstance, value },
      );
    }

    return {
      leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
      operatorV2: operator,
      rightStringMultiValue: value.map((element) => element.value),
      rightFloatMultiValue: EMPTY_ARRAY,
      rightIntMultiValue: EMPTY_ARRAY,
      rightPartyIDMultiValue: EMPTY_ARRAY,
      rightTimeMultiValue: EMPTY_ARRAY,
      rightDateMultiValue: EMPTY_ARRAY,
    };
  },

  // (hchen): Currently this is only used for handling the edge case when flipping the field
  // instance input type from OPTIONAL to REQUESTED (i.e. required) when the a previously OPTIONAL
  // field is set as a native field on a future task. It's not necessary to implement for field
  // types other than date and user, since we only support those as native field now.
  getUpdateFieldInstanceValueInputsFromFieldInstance: (
    _fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): UpdateFieldInstanceValueInputs[] => {
    throw new Error("not implmented yet");
  },

  getValueForFrontend: (
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
    options?: { includeInactiveValues: boolean },
  ): CustomFieldFrontendSelectableOption[] => {
    return translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance, {
      includeInactiveOptions: options?.includeInactiveValues ?? false,
    }).values;
  },

  getTabularValueForFrontend: (fieldInstance: TableFieldInstanceFields) => {
    return translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
  },

  hasAllowedValues: (): boolean => {
    return true;
  },

  isCreateAndEditAllowed: true,

  isFeatureFlagEnabled: (): boolean => {
    return true;
  },

  isFieldInstanceEmpty: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    options?: { includeInactiveOptions: boolean },
  ): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance, {
      includeInactiveOptions: options?.includeInactiveOptions ?? false,
    });
    return frontendFieldInstance.values.length === 0;
  },

  isFieldInstanceValueEqual: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    fieldInstance2: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    const frontendFieldInstance2 = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance2);
    return isArraysEqual(frontendFieldInstance.values, frontendFieldInstance2.values, (a, b) => a.id === b.id);
  },

  isFieldInstanceValueUnchanged: (fieldInstance, proposedChange): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    if (frontendFieldInstance.sourceFieldInstanceId !== proposedChange.sourceFieldInstanceValueId) {
      return false;
    }

    if (frontendFieldInstance.inputType !== proposedChange.inputType) {
      return false;
    }

    if (
      frontendFieldInstance.values.length === 0 &&
      (proposedChange.stringMultiValue == null || proposedChange.stringMultiValue.length === 0)
    ) {
      return true;
    }

    if (
      frontendFieldInstance.values.length === 0 ||
      proposedChange.stringMultiValue == null ||
      proposedChange.stringMultiValue.length === 0
    ) {
      return false;
    }

    // compare translated value with the raw user input value
    return isArraysEqual(
      frontendFieldInstance.values.map((value) => value.displayValue),
      proposedChange.stringMultiValue,
      (a, b) => a === b,
    );
  },

  isFieldInstanceTabularValueUnchanged: (fieldInstance, proposedChange) => {
    const rows = translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);

    const originalRow = rows.find((row) => row.rowNumber === proposedChange.rowNumber);
    if (originalRow == null) {
      return false; // This potentially is a new row
    }

    // compare translated value with the raw user input value
    return isArraysEqual(
      originalRow.value.map((value) => value.displayValue),
      proposedChange.stringMultiValue ?? EMPTY_ARRAY,
      (a, b) => a === b,
    );
  },

  isMultiValued: (): boolean => {
    return true;
  },

  renderDisplayValue: renderDisplayValue,

  renderDisplayValueFromStringValue: renderDisplayValueFromStringValue,

  renderFormField: <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    if (!canProcessField(field)) {
      throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
    }
    const { operator, fieldInstance } = props;
    if (fieldInstance == null) {
      throw new Error("Field instance was not defined. Cannot render form field without field version information.");
    }

    const isDeleted = fieldInstance?.field.deletedAt != null;
    if (operator != null && !isOperatorValid(operator)) {
      console.warn(WARNING_INVALID_OPERATOR_TYPE, {
        field,
        operator,
      });
      return undefined;
    }

    return (
      <RegrelloControlledFormFieldMultiSelect
        {...props}
        key={`${props.controllerProps.name}-${props.keySuffix}`}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        getSelectedItemProps={getSelectedItemProps}
        helperText={t`Select 1 or more.`}
        infoTooltipText={props.description}
        isDeleted={isDeleted}
        isItemsEqual={isItemsEqual}
        itemPredicate={itemPredicate}
        items={extractAllowedValuesFromFieldVersionForFrontend(fieldInstance.spectrumFieldVersion)}
        renderItem={renderItem}
      />
    );
  },

  renderIcon: (props) => {
    return <RegrelloIcon {...props} iconName="multi-select-field" />;
  },

  renderMultipleDisplayValuesForDataGrid: (fieldInstances, options) => {
    if (fieldInstances.length === 0) {
      return null;
    }
    if (fieldInstances.length === 1) {
      return renderDisplayValue(fieldInstances[0], { context: "inlineTruncate" });
    }

    const instancesWithSource = fieldInstances.map((fieldInstance) => {
      return {
        content: renderDisplayValue(fieldInstance, { context: options?.context ?? "table" }),
        workflowName: fieldInstance.workflow?.name,
        stageName: fieldInstance.actionItem?.workflowReference?.stageName,
        taskName: fieldInstance.actionItem?.name,
      };
    });

    return <RegrelloCustomFieldMultiValuePopover instancesWithSource={instancesWithSource} />;
  },

  shouldDisplayMultiValuedToggle: () => false,

  sortComparator,

  validateBulkUploadCellValue: (field, value, options) => {
    const valueAsString = value.trim();

    if (options.isRequired) {
      const result = validateRequired(valueAsString, options.isRequired);
      if (result != null) {
        return result;
      }
    }

    if (valueAsString.length === 0) {
      return {
        value: valueAsString,
      };
    }

    const untranslatedAllowedValueStrings = extractAllowedValues(field, options.fieldVersion);
    const allowedValuesByNormalizedValue = new Map(
      untranslatedAllowedValueStrings.map((valueInternal) => [normalizeSelectValue(valueInternal), valueInternal]),
    );

    const values = valueAsString.split(",").map(trimToken);
    const invalidValues = values.filter((token) => !allowedValuesByNormalizedValue.has(normalizeSelectValue(token)));

    if (invalidValues.length > 0) {
      const numValues = invalidValues.length;
      const commaSeparatedValues = invalidValues.join(", ");
      return {
        value: values.join(","),
        info: [
          {
            message: plural(numValues, {
              one: `Unrecognized value: ${commaSeparatedValues}`,
              other: `Unrecognized values: ${commaSeparatedValues}`,
            }),
            level: "error",
          },
        ],
      };
    }

    return {
      value: values
        .map((multiSelectValue) => allowedValuesByNormalizedValue.get(normalizeSelectValue(multiSelectValue)))
        .join(","),
    };
  },
};

interface FrontendMultiSelectFieldInstance {
  allowedValues: CustomFieldFrontendSelectableOption[];
  crossWorkflowSinksFieldInstanceIds: number[];
  crossWorkflowSourceFieldInstanceId: number | undefined;
  inputType: FieldInstanceValueInputType;
  name: string;
  sinksFieldInstanceIds: number[];
  sourceFieldInstanceId: number | undefined;
  sourceFieldInstanceInputType: FieldInstanceValueInputType | undefined;
  values: CustomFieldFrontendSelectableOption[];
}

function getOptionFrontendDisplayValue(allowedValue: CustomFieldFrontendSelectableOption, suffix: string) {
  return (
    <>
      {allowedValue.displayValue}
      {allowedValue.id === INACTIVE_SELECTABLE_OPTION_ID && <RegrelloInactiveBadge />}
      {suffix}
    </>
  );
}

function getSelectedItemProps(allowedValue: CustomFieldFrontendSelectableOption) {
  return {
    children: renderInactiveSelectedValue(allowedValue),
  };
}

function renderInactiveSelectedValue(allowedValue: CustomFieldFrontendSelectableOption) {
  return <InternalSelectedItem allowedValue={allowedValue} />;
}

/**
 * Renders a selected item that might be inactive (meaning it is no longer
 * included in the allowed values as of this field version).
 */
// eslint-disable-next-line react-refresh/only-export-components
function InternalSelectedItem({ allowedValue }: { allowedValue: CustomFieldFrontendSelectableOption | null }) {
  if (allowedValue == null) {
    return null;
  }

  if (allowedValue.id === INACTIVE_SELECTABLE_OPTION_ID) {
    return (
      <div>
        {allowedValue.displayValue}
        <span className="text-textMuted"> {t`(Inactive)`}</span>
      </div>
    );
  }

  return <div>{allowedValue.displayValue}</div>;
}

function renderItem(allowedValue: CustomFieldFrontendSelectableOption) {
  return {
    key: allowedValue.id,
    text: allowedValue.displayValue,
  };
}

export function isItemsEqual(a: CustomFieldFrontendSelectableOption, b: CustomFieldFrontendSelectableOption) {
  return a.id === b.id;
}

export function itemPredicate(query: string, allowedValue: CustomFieldFrontendSelectableOption) {
  return allowedValue.displayValue.toLocaleLowerCase().includes(query.toLocaleLowerCase());
}

function translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(
  fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
  options?: {
    includeInactiveOptions: boolean;
  },
): FrontendMultiSelectFieldInstance {
  const { field } = fieldInstance;

  if (!canProcessField(field)) {
    throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
  }

  const isFieldInstanceFields = getIsFieldInstanceFields(fieldInstance);

  return extractSelectedValuesOrThrow<FrontendMultiSelectFieldInstance>({
    fieldInstance,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: (allowedValues) => ({
      name: field.name,
      allowedValues,
      inputType: DEFAULT_INPUT_TYPE_IF_NO_VALUE,
      crossWorkflowSinksFieldInstanceIds: EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: undefined,
      sinksFieldInstanceIds: EMPTY_ARRAY,
      sourceFieldInstanceId: undefined,
      sourceFieldInstanceInputType: undefined,
      values: EMPTY_ARRAY,
    }),
    getterIfValue: (fieldInstanceValues, frontendSelectedOptions, allowedValues) => ({
      name: field.name,
      allowedValues,
      inputType: fieldInstanceValues[0].inputType,
      crossWorkflowSinksFieldInstanceIds:
        getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValues[0]) &&
        fieldInstanceValues[0].crossWorkflowSinksFieldInstanceMultiValueString != null
          ? fieldInstanceValues[0].crossWorkflowSinksFieldInstanceMultiValueString.map((value) => value.id)
          : EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValues[0])
        ? fieldInstanceValues[0].crossWorkflowSourceFieldInstanceMultiValueString?.id
        : undefined,
      sinksFieldInstanceIds: isFieldInstanceFields
        ? fieldInstanceValues[0].sinksFieldInstanceMultiValueString?.map(({ id }) => id)
        : EMPTY_ARRAY,
      sourceFieldInstanceId: isFieldInstanceFields
        ? (fieldInstanceValues[0].sourceFieldInstanceMultiValueString?.id ?? undefined)
        : undefined,
      sourceFieldInstanceInputType: isFieldInstanceFields
        ? fieldInstanceValues[0].sourceFieldInstanceMultiValueString?.inputType
        : undefined,
      values: frontendSelectedOptions,
    }),
    options: {
      includeInactiveOptions: options?.includeInactiveOptions ?? false,
    },
  });
}

function isOperatorValid(
  operator: ConditionOperator,
): operator is "EMPTY" | "NOT_EMPTY" | "CONTAINS_ALL_OF" | "CONTAINS_ANY_OF" | "CONTAINS_NONE_OF" {
  return getConditionOperators().find((stageStartOperator) => stageStartOperator.operator === operator) != null;
}

function isFieldAllowedValueString(
  value: FieldFields["allowedValues"][0],
): value is FieldAllowedValueString & { __typename: "FieldAllowedValueString" } {
  return value.__typename === "FieldAllowedValueString";
}

function isValueValid(value: unknown): value is Array<{ id: number; value: string }> | null {
  return (
    value == null ||
    (Array.isArray(value) &&
      value.every(
        // this still returns true if value is an empty array.
        (element) => typeof element === "object" && typeof element.id === "number" && typeof element.value === "string",
      ))
  );
}

function isMultiSelectFrontendValue(value: unknown): value is MultiSelectPluginFrontendValue {
  return (
    Array.isArray(value) &&
    value.every(
      // this still returns true if value is an empty array.
      (element) =>
        typeof element === "object" &&
        typeof element.id === "number" &&
        typeof element.value === "string" &&
        typeof element.displayValue === "string",
    )
  );
}

function translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance: TableFieldInstanceFields) {
  const spectrumFieldVersion = fieldInstance.spectrumFieldVersion;
  if (spectrumFieldVersion == null) {
    return EMPTY_ARRAY;
  }
  const allowedValues = extractAllowedValuesFromFieldVersionForFrontend(spectrumFieldVersion);
  const allowedValuesByValue = new Map(allowedValues.map((value) => [value.value, value]));

  return extractMultipleTabularArraysOrThrow({
    fieldInstance,
    fieldInstanceValueTypeName: "FieldInstanceMultiValueString",
    errorMessageIfMultipleValues: t`Multi-select tabular values cannot have more than one array of values.`,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => EMPTY_ARRAY,
    getterIfTabularValue: (fieldInstanceValue: FieldInstanceValueFieldsFieldInstanceMultiValueString) => {
      if (fieldInstanceValue.tabularMultiValueString == null) {
        return EMPTY_ARRAY;
      }

      const rowDatas: Array<{ rowNumber: number; value: CustomFieldFrontendSelectableOption[] }> = [];

      for (const row of fieldInstanceValue.tabularMultiValueString) {
        const rowResultValues: CustomFieldFrontendSelectableOption[] = [];

        for (const value of row.value) {
          const allowedValue = allowedValuesByValue.get(value);
          if (allowedValue == null) {
            rowResultValues.push({
              id: -1,
              displayValue: value ?? EMPTY_STRING,
              value: value ?? EMPTY_STRING,
            });
          } else {
            rowResultValues.push({
              id: allowedValue.id,
              displayValue: allowedValue.displayValue,
              value: allowedValue.value,
            });
          }
        }

        rowDatas.push({
          rowNumber: row.rowNumber,
          value: rowResultValues,
        });
      }

      return rowDatas;
    },
  });
}
